import { useState } from "react";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { toast } from "sonner";

const INVOLVED_IMG = "https://images.unsplash.com/photo-1779588160719-76e1b626f762?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxhbWVyaWNhbiUyMHNtYWxsJTIwdG93biUyMGNvbW11bml0eSUyMGV2ZW50fGVufDB8fHx8MTc4MDE5NDkxN3ww&ixlib=rb-4.1.0&q=85";

const INTEREST_OPTIONS = [
  "Door knocking",
  "Phone banking",
  "Yard sign distribution",
  "Hosting an event",
  "Social media",
  "Wherever needed",
];

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-2">
        {label} {required && <span className="text-accent">*</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls = "w-full bg-background border border-input px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";

function VolunteerForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", zip_code: "", message: "" });
  const [interests, setInterests] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleInterest = (i) => {
    setInterests((cur) => (cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i]));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/volunteers", { ...form, interests });
      toast.success("Thank you! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", zip_code: "", message: "" });
      setInterests([]);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="volunteer-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Full Name" required>
          <input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="volunteer-name" />
        </Field>
        <Field label="Email" required>
          <input type="email" className={inputCls} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="volunteer-email" />
        </Field>
        <Field label="Phone">
          <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="volunteer-phone" />
        </Field>
        <Field label="ZIP Code">
          <input className={inputCls} value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="volunteer-zip" />
        </Field>
      </div>
      <div>
        <span className="block text-xs uppercase tracking-[0.18em] font-bold text-primary mb-3">How would you like to help?</span>
        <div className="flex flex-wrap gap-2">
          {INTEREST_OPTIONS.map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => toggleInterest(i)}
              data-testid={`interest-${i.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-2 text-sm border transition-colors ${
                interests.includes(i)
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border bg-card text-foreground hover:border-primary"
              }`}
            >
              {i}
            </button>
          ))}
        </div>
      </div>
      <Field label="Anything else?">
        <textarea rows="4" className={inputCls} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="volunteer-message" />
      </Field>
      <button
        type="submit"
        disabled={submitting}
        data-testid="volunteer-submit"
        className="bg-accent text-accent-foreground px-7 py-3.5 font-bold uppercase tracking-wider hover:bg-[#8A1333] transition-colors disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Sign Me Up"}
      </button>
    </form>
  );
}

function YardSignForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "South Lebanon", zip_code: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/yard-signs", form);
      toast.success("Your yard sign is on the way!");
      setForm({ name: "", email: "", phone: "", address: "", city: "South Lebanon", zip_code: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="yard-sign-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Full Name" required>
          <input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="yardsign-name" />
        </Field>
        <Field label="Email" required>
          <input type="email" className={inputCls} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="yardsign-email" />
        </Field>
        <Field label="Phone">
          <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="yardsign-phone" />
        </Field>
        <Field label="ZIP Code" required>
          <input className={inputCls} required value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} data-testid="yardsign-zip" />
        </Field>
      </div>
      <Field label="Street Address" required>
        <input className={inputCls} required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="yardsign-address" />
      </Field>
      <Field label="City">
        <input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="yardsign-city" />
      </Field>
      <button
        type="submit"
        disabled={submitting}
        data-testid="yardsign-submit"
        className="bg-primary text-primary-foreground px-7 py-3.5 font-bold uppercase tracking-wider hover:bg-[#072448] transition-colors disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Request a Yard Sign"}
      </button>
    </form>
  );
}

export default function Volunteer() {
  const [tab, setTab] = useState("volunteer");

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: `url(${INVOLVED_IMG})` }} aria-hidden />
        <div className="absolute inset-0 hero-overlay" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="flex items-center gap-3 mb-5">
            <div className="flag-rule"><span /><span /><span /></div>
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-accent">Get Involved</span>
          </div>
          <h1 className="font-serif text-5xl md:text-6xl font-black text-primary leading-[0.95] tracking-tight max-w-3xl">
            South Lebanon's future starts with you.
          </h1>
          <p className="mt-5 text-lg md:text-xl text-foreground/85 max-w-2xl leading-relaxed">
            Volunteer your time, request a yard sign, or join our campaign updates. Every neighbor matters.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex gap-0 border border-border mb-10">
            <button
              onClick={() => setTab("volunteer")}
              data-testid="tab-volunteer"
              className={`flex-1 py-4 font-bold uppercase tracking-wider text-sm transition-colors ${
                tab === "volunteer" ? "bg-primary text-primary-foreground" : "bg-card text-primary hover:bg-secondary"
              }`}
            >
              Volunteer
            </button>
            <button
              onClick={() => setTab("yardsign")}
              data-testid="tab-yardsign"
              className={`flex-1 py-4 font-bold uppercase tracking-wider text-sm transition-colors border-l border-border ${
                tab === "yardsign" ? "bg-primary text-primary-foreground" : "bg-card text-primary hover:bg-secondary"
              }`}
            >
              Yard Sign
            </button>
          </div>

          <div className="bg-card border border-border p-8 md:p-10">
            {tab === "volunteer" ? <VolunteerForm /> : <YardSignForm />}
          </div>
        </div>
      </section>
    </div>
  );
}
